// Revolt Icon Component
// Unified icon component supporting multiple icon libraries
// Supports feather, lucide, mdi, and custom icon sources

import React, { useEffect, useRef } from 'react';
import './RevoltIcon.css';

export function RevoltIcon({
  name,
  source = 'feather', // 'feather', 'lucide', 'mdi', or custom source
  size = 'default', // 'xs', 'sm', 'default', 'lg', 'xl' or number
  className = '',
  color,
  style = {},
  ...props
}) {
  const iconRef = useRef(null);
  const [iconSource, iconName] = name.includes(':') ? name.split(':') : [source, name];

  // Convert size to pixel value
  const getSizeValue = (size) => {
    const sizeMap = {
      'xs': '12px',
      'sm': '14px',
      'default': '16px',
      'lg': '20px',
      'xl': '24px'
    };
    return sizeMap[size] || (typeof size === 'number' ? `${size}px` : size);
  };

  const sizeValue = getSizeValue(size);

  // Build CSS classes
  const iconClasses = [
    'revolt-icon',
    `revolt-icon-${iconSource}`,
    className
  ].filter(Boolean).join(' ');

  // Build inline styles
  const iconStyles = {
    width: sizeValue,
    height: sizeValue,
    color: color,
    ...style
  };

  // Initialize feather and lucide icons
  useEffect(() => {
    if (iconSource === 'feather' && typeof window !== 'undefined' && window.feather && iconRef.current) {
      try {
        window.feather.replace();
      } catch (e) {
        // Ignore feather initialization errors
      }
    }

    if (iconSource === 'lucide' && typeof window !== 'undefined' && window.lucide && iconRef.current) {
      try {
        window.lucide.createIcons();
      } catch (e) {
        // Ignore lucide initialization errors
      }
    }
  }, [iconSource, iconName]);

  // Render based on icon source
  if (iconSource === 'feather') {
    return (
      <span
        ref={iconRef}
        className={iconClasses}
        style={iconStyles}
        {...props}
      >
        <i data-feather={iconName}></i>
      </span>
    );
  }

  if (iconSource === 'lucide') {
    return (
      <span
        ref={iconRef}
        className={iconClasses}
        style={iconStyles}
        {...props}
      >
        <i data-lucide={iconName}></i>
      </span>
    );
  }

  if (iconSource === 'mdi') {
    return (
      <span
        className={iconClasses}
        style={iconStyles}
        {...props}
      >
        <i className={`mdi mdi-${iconName}`}></i>
      </span>
    );
  }

  // Custom icon source - assume it's a CSS class
  return (
    <span
      className={`${iconClasses} ${iconSource}-${iconName}`}
      style={iconStyles}
      {...props}
    >
      <i className={`${iconSource} ${iconSource}-${iconName}`}></i>
    </span>
  );
}

// Backward compatibility alias
export const Icon = RevoltIcon;
